// repeat "최소 1행 필수"(required) 토글의 노출 게이트 — 순수 함수.
//
// required는 행 0개를 제출·완료 차단으로 만드는 검증 축(shared walkCheckableInputs — 구 DLR
// "작업 행 ≥1" 게이트의 일반화)이라, **작성자가 행을 추가할 수 없는 소스**에 걸면 "차단됐는데
// 채울 방법이 없는" 막다른 게이트가 된다(리뷰 r0824b code W1).
//
// 판정 축은 rc RepeatRowsBlock의 행 추가 성립과 같다(composerWidgetTreeRender:
// `dsDef ? effectiveRowAddRemove(dsDef).add : false` — dataSources 미해석 시 dataDefs의 compute
// 정의를 **표시 전용** 폴백으로 인정, 행 추가 UI 자체가 없음). 추가/삭제가 별개 축이 된 뒤
// (2026-09-14 — shared `effectiveRowAddRemove`, allowAddRemove 상위 스위치 + allowAdd/allowRemove)
// 이 게이트는 **add 축만** 본다 — 삭제만 막은 소스는 여전히 행을 채울 수 있으니 게이트를 연다:
//  - dataSources의 편집형 list 선언(editMode!=='readonly' 기본 editable, add 축 true 기본)만 true
//  - compute 파생 dataDef 폴백·미해석 key(dataSources 부재) / editMode:'readonly' /
//    allowAddRemove:false / allowAdd:false / cardinality:'single'은 false
//  - allowRemove:false는 판정과 무관(삭제 축은 행을 채우는 능력이 아니다)
// (editMode 축은 rc 판정보다 엄격 — P3 표준(readonly 자식 소스 = allowAddRemove:false 동반)이
// 지켜지지 않은 선언에도 막다른 게이트를 열지 않기 위함.)
//
// 게이트가 닫힌 채 required가 저장돼 있을 때의 안내 강도는 judgeRepeatRequiredMinRow가 정한다
// (조립형·삭제만 허용 소스는 행이 prefill에서 오므로 red 오류가 아니라 amber 안내 — 아래 JSDoc).
import { effectiveRowAddRemove, type DataSourceDef } from '@safetyhub/shared';

/** repeat.over 소스가 "작성자가 행을 추가할 수 있는 편집형"인지 — required 토글 노출 게이트. */
export function canRepeatRequireMinRow(
  over: string,
  dataSources: DataSourceDef[] | undefined,
): boolean {
  const def = (dataSources ?? []).find((d) => d.key === over);
  if (!def) return false; // compute 파생 dataDef 폴백/미해석 — 작성폼에 행 추가 UI 없음
  return (
    def.cardinality === 'list' &&
    (def.editMode ?? 'editable') !== 'readonly' &&
    effectiveRowAddRemove(def).add
  );
}

/**
 * 닫힌 게이트의 안내 강도 판정 결과 — 저작 판정 유틸 공통 어휘(`kind` 판별자 + 사유 객체 ·
 * shared `judgeRowScopeWriteBack`·admin `judgeInspectionSelectionMulti`와 같은 형상).
 *  - `eligible`: 게이트 충족(토글 노출) — 안내 대상이 아니다.
 *  - `ineligible` + `reason`: 아래 `judgeRepeatRequiredMinRow` JSDoc의 두 갈래.
 */
export type RepeatRequiredGateVerdict =
  | { kind: 'eligible' }
  | { kind: 'ineligible'; reason: 'seeded-remove-only' | 'dead-end' };

/**
 * 게이트 미충족인데 `required:true`가 저장돼 있을 때 패널이 낼 안내의 **강도** — 순수 판정.
 *
 * 종전에는 게이트가 닫히면 전부 red(「영구 차단 — 필수를 해제하거나…」+ 해제 유도)였는데, 그 문장은
 * **행이 어디서 오는가**를 묻지 않는다. 조립형(kind:'assembled') 편집 소스는 prefill이 행을 seed하므로
 * 작성자가 추가하지 못해도 행은 채워진다 — 위험성평가 결과서 항목(`ra-items-src`: 발견사항별 1행,
 * `allowAdd:false` + 삭제만)이 그 형상이고, 거기서 required는 「전부 지우면 제출 차단」(구 「포함할
 * 항목을 하나 이상 선택해주세요」)이라는 **의도된** 검증이다. red 안내대로 「필수 해제」를 누르면 0행
 * 제출이 열려 의도와 반대가 된다(5차 은닉 감사, 2026-09-14). 그래서 둘로 가른다:
 *  - `'seeded-remove-only'`(amber 안내): assembled · list · editable · 삭제 허용 · 추가 불허. 조립
 *    결과가 0행(발견사항 없음)이면 작성자가 채울 수 없어 제출 불가 — 그것이 의도면 유지. 해제 버튼은
 *    두되 권고하지 않는다.
 *  - `'dead-end'`(red — 종전): 그 밖의 전부. manual(작성자가 손으로 채우는 데이터셋)에서 추가를 막고
 *    required를 걸면 행을 채울 길이 없고, readonly·상위 스위치 off·compute 파생·single도 같다.
 *
 * `kind`가 판정 축이다(`primary` 유무가 아니라): shared JSDoc대로 `primary`는 assembled에도 ''/부재가
 * 섞여 있어 소스 판별에 쓸 수 없다.
 */
export function judgeRepeatRequiredMinRow(
  over: string,
  dataSources: DataSourceDef[] | undefined,
): RepeatRequiredGateVerdict {
  if (canRepeatRequireMinRow(over, dataSources)) return { kind: 'eligible' };
  const def = (dataSources ?? []).find((d) => d.key === over);
  if (!def) return { kind: 'ineligible', reason: 'dead-end' };
  const addRemove = effectiveRowAddRemove(def);
  const seededRemoveOnly =
    def.kind === 'assembled' &&
    def.cardinality === 'list' &&
    (def.editMode ?? 'editable') !== 'readonly' &&
    addRemove.remove &&
    !addRemove.add;
  return { kind: 'ineligible', reason: seededRemoveOnly ? 'seeded-remove-only' : 'dead-end' };
}
